import { ReactNode } from "react";
import { cn } from "@/utils/cn";

export type BadgeTone = "gray" | "blue" | "green" | "amber" | "red" | "purple" | "indigo" | "teal";

const TONES: Record<BadgeTone, string> = {
  gray: "bg-gray-100 text-gray-700",
  blue: "bg-blue-50 text-blue-700",
  green: "bg-green-50 text-green-700",
  amber: "bg-amber-50 text-amber-800",
  red: "bg-red-50 text-red-700",
  purple: "bg-purple-50 text-purple-700",
  indigo: "bg-indigo-50 text-indigo-700",
  teal: "bg-teal-50 text-teal-700",
};

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  icon?: ReactNode;
  title?: string;
  className?: string;
}

/** Small status pill. Pages hand-roll these today (status configs per domain). */
export function Badge({ children, tone = "gray", icon, title, className }: BadgeProps) {
  return (
    <span title={title} className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap", TONES[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

const DOTS: Record<BadgeTone, string> = {
  gray: "bg-gray-400",
  blue: "bg-blue-500",
  green: "bg-green-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  indigo: "bg-indigo-500",
  teal: "bg-teal-500",
};

/** A badge's state as a dot in its tone, where a phone has no room for the pill (B13). The label is its accessible name and
 * its title. */
export function BadgeDot({ tone = "gray", label, className }: { tone?: BadgeTone; label: string; className?: string }) {
  return <span role="img" aria-label={label} title={label} className={cn("inline-block w-2 h-2 shrink-0 rounded-full", DOTS[tone], className)} />;
}
